import { useContext, useState, ReactNode } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { LanguageContext } from "context";

interface Props {
  image: string;
  title: string;
  description: string;
  touristAttractions: string;
  specialtyProducts: string;
  action?: ReactNode | boolean;
  isThumbUp?: boolean;
  isThumbDown?: boolean;
  onThumbUp?: () => void;
  onThumbDown?: () => void;
  disabledThumb?: boolean; 
  [key: string]: any;
}

function PrefecturesCard({
  image,
  title,
  description,
  touristAttractions,
  specialtyProducts,
  action,
  isThumbUp,
  isThumbDown,
  onThumbUp,
  onThumbDown,
  disabledThumb
}: Props): JSX.Element {
  const { t } = useContext(LanguageContext) as any;

  const [openDetail, setOpenDetail] = useState(false);
  const [openTourist, setOpenTourist] = useState(false);
  const [openSpecialty, setOpenSpecialty] = useState(false);

  return (
    <>
      <Card
        sx={{
          "&:hover .card-header": {
            transform: action && "translate3d(0, -50px, 0)",
          },
          width: "100%",
          height: "100%",
          minHeight: "420px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <MDBox
          position="relative"
          borderRadius="lg"
          mt={-3}
          mx={2}
          className="card-header"
          sx={{
            height: "310px",
            overflow: "hidden",
            backgroundColor: "#E3F2FD",
            transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)",
          }}
        >
          {/* 画像本体 */}
          <MDBox
            component="img"
            src={image}
            alt={title}
            width="100%"
            height="100%"
            position="relative"
            zIndex={1}
            sx={{
              objectFit: "contain",
              objectPosition: "center",
              padding: "16px", // 地図が端に寄りすぎないように少し余白を持たせます
            }}
          />

          {/* タイトルを画像下部に重ねて表示 */}
          <MDBox position="absolute" bottom={8} left={0} width="100%" textAlign="center" zIndex={2}>
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#000000",
                backgroundColor: "#E3F2FD",
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
              }}
            >
              {title}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox textAlign="center" pt={3} px={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={action ? -8 : -4.25}
          >
            {action}
          </MDBox>
          <MDTypography variant="h6" fontWeight="regular" sx={{ mt: 4 }}>
            {title}{t("PrefecturesCard.stay")}
          </MDTypography>
        </MDBox>
        <Divider />
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={0.5}
          pb={3}
          px={3}
          lineHeight={1}
          gap={1}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            {onThumbUp && (
              <Tooltip title={t("PrefecturesCard.good")}>
                <Icon
                  color={isThumbUp ? "info" : "inherit"}
                  sx={{
                    cursor: disabledThumb ? "not-allowed" : "pointer",
                    opacity: disabledThumb ? 0.5 : 1,
                  }}
                  onClick={disabledThumb ? undefined : onThumbUp}
                >
                  thumb_up
                </Icon>
              </Tooltip>
            )}
            {onThumbDown && (
              <Tooltip title={t("PrefecturesCard.bad")}>
                <Icon
                  color={isThumbDown ? "error" : "inherit"}
                  sx={{
                    cursor: disabledThumb ? "not-allowed" : "pointer",
                    opacity: disabledThumb ? 0.5 : 1,
                  }}
                  onClick={disabledThumb ? undefined : onThumbDown}
                >
                  thumb_down
                </Icon>
              </Tooltip>
            )}
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
            <MDButton 
              variant="gradient" 
              color="info" 
              size="small" 
              onClick={() => setOpenDetail(true)}
              sx={{ padding: "4px 8px", fontSize: "0.7rem", minWidth: "auto" }}
            >
              {t("PrefecturesCard.title1")}
            </MDButton>
            <MDButton 
              variant="gradient" 
              color="info" 
              size="small" 
              onClick={() => setOpenTourist(true)}
              sx={{ padding: "4px 8px", fontSize: "0.7rem", minWidth: "auto" }}
            >
              {t("PrefecturesCard.title2")}
            </MDButton>
            <MDButton 
              variant="gradient" 
              color="info" 
              size="small" 
              onClick={() => setOpenSpecialty(true)}
              sx={{ padding: "4px 8px", fontSize: "0.7rem", minWidth: "auto" }}
            >
              {t("PrefecturesCard.title3")}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("PrefecturesCard.title1")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" sx={{ whiteSpace: "pre-wrap" }}>
            {description}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={() => setOpenDetail(false)}>
            {t("PrefecturesCard.close")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 観光ダイアログ */}
      <Dialog open={openTourist} onClose={() => setOpenTourist(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("PrefecturesCard.title2")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" sx={{ whiteSpace: "pre-wrap" }}>
            {touristAttractions || t("PrefecturesCard.no-data")}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={() => setOpenTourist(false)}>
            {t("PrefecturesCard.close")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 特産品ダイアログ */}
      <Dialog open={openSpecialty} onClose={() => setOpenSpecialty(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("PrefecturesCard.title3")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" sx={{ whiteSpace: "pre-wrap" }}>
            {specialtyProducts || t("PrefecturesCard.no-data")}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={() => setOpenSpecialty(false)}>
            {t("PrefecturesCard.close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

PrefecturesCard.defaultProps = {
  action: false,
};

export default PrefecturesCard;